/**
 * Route guards (audit W3 / Phase 1 sweep). Used as layout routes in App.js:
 *
 *   <Route element={<RequireAuth />}>      ...patron pages that need a login
 *   <Route element={<RequireBusiness />}>  ...advertiser portal
 *   <Route element={<RequireVenue />}>     ...venue partner portal
 *   <Route element={<RequireAdmin />}>     ...admin portal
 *
 * Each renders <Outlet /> when allowed. While the auth context is still
 * loading they render a small spinner so a refresh never flashes a redirect.
 * Pages keep their own richer "no access" / "pending review" screens; the
 * guards only decide *whether a session exists* for the surface.
 */
import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getToken, UNAUTHORIZED_EVENT } from '../../lib/api';
import { hasBusinessAccess, hasVenueAccess, hasAdminAccess } from '../../lib/roles';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }} role="status" aria-live="polite">
    <Loader2 className="animate-spin" size={40} style={{ color: '#8b5cf6' }} aria-hidden="true" />
    <span className="sr-only">Loading</span>
  </div>
);

// Role checks live in lib/roles.js (Phase 5a); re-exported here for existing
// importers (Redeem.jsx) and used by the Require* guards below.
export { hasBusinessAccess, hasVenueAccess, hasAdminAccess };

/** Patron pages: prompt the login modal and come back here afterwards. */
export const RequireAuth = () => {
  const { isAuthenticated, loading, openAuthModal, setPostAuthRedirect } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setPostAuthRedirect?.(location.pathname + location.search);
      openAuthModal?.('login');
    }
  }, [loading, isAuthenticated]);
  if (loading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <Outlet />;
};

/** Advertiser portal: a signed-in session is required; the pages decide what an
 *  account without a business sees (pricing, onboarding, pending review). */
export const RequireBusiness = () => {
  const { isAuthenticated, loading, openAuthModal, setPostAuthRedirect } = useAuth();
  const location = useLocation();
  const legacyToken = !!getToken('business');
  useEffect(() => {
    if (!loading && !isAuthenticated && !legacyToken) {
      setPostAuthRedirect?.(location.pathname + location.search);
      openAuthModal?.('login');
    }
  }, [loading, isAuthenticated, legacyToken]);
  if (loading) return <Spinner />;
  if (!isAuthenticated && !legacyToken) return <Navigate to="/" replace />;
  return <Outlet />;
};

/** Venue partner portal. */
export const RequireVenue = () => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!isAuthenticated || !hasVenueAccess(user)) return <Navigate to="/venue/login" replace />;
  return <Outlet />;
};

/** Admin portal: the classic admin login stores `adminToken` outside the auth
 *  context, so a stored admin token is enough; otherwise the session user must
 *  carry an admin role. */
export const RequireAdmin = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const adminToken = !!getToken('admin');
  if (loading && !adminToken) return <Spinner />;
  if (!adminToken && !(isAuthenticated && hasAdminAccess(user))) return <Navigate to="/admin/login" replace />;
  return <Outlet />;
};

/** Mounted once in App: when any client gets a 401 for a portal surface, send
 *  the user to that portal's login instead of leaving a half-rendered page. */
export const UnauthorizedListener = () => {
  const location = useLocation();
  useEffect(() => {
    const onUnauthorized = (e) => {
      const surface = e.detail?.surface;
      const path = location.pathname;
      if (surface === 'admin' && path.startsWith('/admin') && path !== '/admin/login') {
        try { localStorage.removeItem('adminToken'); } catch {}
        window.location.assign('/admin/login');
      } else if (surface === 'venue' && path.startsWith('/venue') && path !== '/venue/login') {
        window.location.assign('/venue/login');
      }
      // Patron / business 401s are handled by the auth context (token refresh / modal).
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [location.pathname]);
  return null;
};
